import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { createRoot } from 'react-dom/client';
import { App as AntApp, ConfigProvider } from 'antd';
import { DesktopOutlined } from '@ant-design/icons';
import heIL from 'antd/locale/he_IL';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import App from './App';
import { AntdBridge } from './components/antdBridge';
import { setErrorReporter } from './api/client';
import { notifyError } from './components/errors';
import './theme/global.css';
// Wire the API client to surface any unhandled business error automatically.
// Individual calls can opt out per-code (see api/index.ts wrappers). This
// registration is idempotent and fires once at boot.
setErrorReporter(notifyError);
const container = document.getElementById('root');
if (!container)
    throw new Error('root element missing');
// Mobile-app primary violet (apps/mobile/src/theme/colors.ts:58 — `palette.primary`).
// Harmonizes AntD primary accents (links, primary buttons, selected menu, focus)
// with the app's identity.
const APP_PURPLE = '#8B2FD6';
const APP_PURPLE_DEEP = '#6B21A8';
const APP_LAVENDER = '#F1EAFF';
const APP_LAVENDER_ALT = '#EFE9FA';
createRoot(container).render(_jsx(React.StrictMode, { children: _jsx(ConfigProvider, { locale: heIL, direction: "rtl", theme: {
            token: {
                colorPrimary: APP_PURPLE,
                colorLink: APP_PURPLE,
                colorBgLayout: APP_LAVENDER,
                borderRadius: 12,
                fontFamily: "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', " +
                    "'Helvetica Neue', system-ui, Arial, sans-serif",
            },
            components: {
                // Pill-shaped buttons (medium radius for icon-only; the global
                // `.btn-action-round` class handles the rounded-square variant).
                Button: {
                    borderRadius: 999,
                    borderRadiusLG: 999,
                    borderRadiusSM: 999,
                    controlHeight: 36,
                    fontWeight: 500,
                },
                Card: {
                    borderRadiusLG: 16,
                },
                Menu: {
                    itemBorderRadius: 999,
                    itemSelectedBg: APP_LAVENDER_ALT,
                    itemSelectedColor: APP_PURPLE_DEEP,
                    itemHoverBg: 'rgba(139, 47, 214, 0.06)',
                    itemActiveBg: APP_LAVENDER_ALT,
                    iconMarginInlineEnd: 10,
                },
                Table: {
                    headerBg: APP_LAVENDER,
                    headerColor: APP_PURPLE_DEEP,
                    borderColor: 'rgba(107, 33, 168, 0.08)',
                    cellPaddingBlock: 12,
                },
                Modal: {
                    borderRadiusLG: 16,
                },
                Input: {
                    borderRadius: 10,
                },
                Select: {
                    borderRadius: 10,
                },
                Tag: {
                    borderRadiusSM: 999,
                },
            },
        }, children: _jsxs(AntApp, { children: [_jsx(AntdBridge, {}), _jsx("div", { className: "admin-small-screen-blocker", role: "dialog", "aria-modal": "true", dir: "rtl", children: _jsxs("div", { className: "admin-small-screen-blocker__card", children: [_jsx(DesktopOutlined, { className: "admin-small-screen-blocker__icon", "aria-hidden": true }), _jsx("h2", { className: "admin-small-screen-blocker__title", children: "\u05D4\u05DE\u05E2\u05E8\u05DB\u05EA \u05D6\u05DE\u05D9\u05E0\u05D4 \u05D1\u05DE\u05D7\u05E9\u05D1 \u05D1\u05DC\u05D1\u05D3" }), _jsx("p", { className: "admin-small-screen-blocker__body", children: "\u05E4\u05D0\u05E0\u05DC \u05D4\u05E0\u05D9\u05D4\u05D5\u05DC \u05D0\u05D9\u05E0\u05D5 \u05DE\u05D5\u05EA\u05D0\u05DD \u05DC\u05E9\u05D9\u05DE\u05D5\u05E9 \u05DE\u05D4\u05E0\u05D9\u05D9\u05D3. \u05D9\u05E9 \u05DC\u05D4\u05D9\u05DB\u05E0\u05E1 \u05DE\u05D3\u05E4\u05D3\u05E4\u05DF \u05D1\u05DE\u05D7\u05E9\u05D1." })] }) }), _jsx(BrowserRouter, { children: _jsx(AuthProvider, { children: _jsx(App, {}) }) })] }) }) }));
